import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as PdfIcon } from '../../assets/icons/pdf.svg';
import { NumericFormat } from 'react-number-format';
import Button from '../Button/Button';
import FormulaireAdministratif from '../FormulaireAdministratif/FormulaireAdministratif';
import ValidationPopUp from '../ValidationPopUp/ValidationPopUp';
import DropDownSignataires from '../DropDownSignataires/DropDownSignataires';

import './Candidature.css'

interface NumberFormatValues {
  formattedValue: string;
  value: string;
  floatValue?: number;
}

interface CandidatureProps {
  commande: any;
  setCommande: React.Dispatch<React.SetStateAction<any>>;
  handleExport: () => void;
  apiData?: any; // Nouvelle prop pour les données de l'API
}

const Candidature: React.FC<CandidatureProps> = ({
  commande,
  setCommande,
  handleExport,
  apiData
}) => {
  const navigate = useNavigate();
  const [departementInfo, setDepartementInfo] = useState<any>(null);

  // Charger les informations du département depuis apiData si disponible
  useEffect(() => {
    if (apiData?.status === 'success' && apiData?.data) {
      setDepartementInfo(apiData.data);

      // Récupérer les données de l'opération depuis apiData
      const operationData = apiData.data.operation;

      // Mettre à jour la commande avec les informations complètes
      setCommande(prev => ({
        ...prev,
        operation: operationData, // Conserver toutes les données brutes de l'opération
        formulaire: {
          ...prev?.formulaire,
          // Données administratives
          nomOperation: operationData.nom || "Pas précisé",
          referenceInterne: operationData.reference || "Pas précisé", 
          adresse: operationData.adresse || "Pas précisé",
          codePostal: operationData.code_postal || "Pas précisé",
          ville: operationData.ville || "Pas précisé",
          coordonneesMaitriseDOuvrage: operationData.coordonnees_maitrise_ouvrage || "Pas précisé",
          coordonneesMaitrise: operationData.coordonnees_maitrise || "Pas précisé",
          facturation: operationData.facturation || "Pas précisé",
          delaisConsultation: 10,
          delaisFinOperation: operationData.delais || 30,
          visitePreavis: operationData.visite === true,
          conditionVisite: operationData.conditionVisite || "Pas précisé",
          besoin: operationData.besoin || "Pas précisé",
          dateDebutConsultation: new Date().toISOString().split('T')[0],
          dateDebutOperation: operationData.date_debut_operation || "Pas précisé",
          dateFinOperation: operationData.date_fin_operation || "Pas précisé",
          datePrevisionelle: operationData.expire_at || "Pas précisé",
          
          // Données géographiques
          lotGeographique: apiData.data.nom || "Pas précisé",
          regionName: apiData.data.region?.nom || "Pas précisé",

          // Ajouter l'objet operation complet pour accès facile
          operation: {
            ...operationData,
            visite: operationData.visite === true ? "Oui" : "Non",
            conditionVisite: operationData.visite === true ? (operationData.conditionVisite || "Pas précisé") : ""
          }
        }
      }));
    }
  }, [apiData, setCommande]);

  const [criteresErrors, setCriteresErrors] = useState<{ [key: string]: string }>({});
  const [criteresSaving, setCriteresSaving] = useState(false);
  const [criteresSuccessMsg, setCriteresSuccessMsg] = useState('');
  const [criteresLoading, setCriteresLoading] = useState(false);

  // État pour gérer le modal de confirmation de candidature
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  // Initialisation des critères
  const criteresFormData = {
    criteres: {
      organisation: {
        reponse: commande.criteres?.organisation?.reponse || ''
      },
      moyens: {
        reponse: commande.criteres?.moyens?.reponse || ''
      },
      social: {
        reponse: commande.criteres?.social?.reponse || ''
      }
    }
  };

  // Récupération des données de la commande (API)
  useEffect(() => {
    const fetchCriteres = async () => {
      // Ajout d'un guard pour éviter les requêtes multiples
      if (criteresLoading) return;
      
      setCriteresLoading(true);
      try {
        const commandeId = window.location.pathname.split('/').pop() || '';
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/subsequents/commande/${commandeId}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        if (!response.ok) throw new Error('Erreur lors du chargement des critères');
        const data = await response.json();
        
        // Utiliser une fonction callback pour éviter les problèmes de stale closure
        setCommande(prev => {
          // Ne pas mettre à jour si les données sont identiques
          if (prev?.id === data.data.id && 
              prev?.criteres?.organisation?.reponse === data.data.note_technique?.description_humain) {
            return prev;
          }
          
          return {
            ...prev!,
            criteres: {
              organisation: {
                reponse: data.data.note_technique?.description_humain || ''
              },
              moyens: {
                reponse: data.data.note_technique?.description_operationnel || ''
              },
              social: {
                reponse: data.data.note_technique?.description_environnement || ''
              }
            },
            reponseTechnique: data.data.reponse || '',
            articlesPrestataire: (data.data.articles || []).map((art: any) => ({
              id: art.id,
              prixHT: art.pivot.prix ?? 0
            })),
            // Préserver l'état du signataire s'il existe déjà
            id_signataire: prev?.id_signataire || data.data.id_signataire,
            type_signataire: prev?.type_signataire || data.data.type_signataire
          };
        });
      } catch (e) {
        console.error(e);
      } finally {
        setCriteresLoading(false);
      }
    };

    fetchCriteres();
    // Supprimer la dépendance vide pour ne s'exécuter qu'une seule fois
  }, []);

  // Calcul du total HT dynamique
  const [totalHT, setTotalHT] = useState(0);

  useEffect(() => {
    const total = (commande.articlesPrestataire || [])
      .reduce((sum, art) => sum + (art?.prixHT || 0), 0);
    setTotalHT(total);
  }, [commande.articlesPrestataire]);

  // Handler pour les champs critères
  const handleCriteresChange = (critere: 'organisation' | 'moyens' | 'social', value: string) => {
    setCommande(prev => ({
      ...prev!,
      criteres: {
        ...prev!.criteres,
        [critere]: {
          ...prev!.criteres?.[critere],
          reponse: value
        }
      }
    }));
    setCriteresErrors(prev => ({ ...prev, [critere]: '' }));
  };

  // Validation des critères
  const validateCriteresForm = () => {
    const newErrors: { [key: string]: string } = {};
    (['organisation', 'moyens', 'social'] as const).forEach((critere) => {
      if (!commande.criteres?.[critere]?.reponse?.trim()) {
        newErrors[critere] = 'Ce champ est requis';
      }
    });
    setCriteresErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Sauvegarde en brouillon (API)
  const handleCriteresSaveDraft = async () => {
    setCriteresSaving(true);
    setCriteresSuccessMsg('');
    try {
      const commandeId = window.location.pathname.split('/').pop() || '';
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/subsequents/${commandeId}/criteres`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          criteres: commande.criteres
        })
      });
      if (!response.ok) throw new Error('Erreur lors de la sauvegarde');
      setCriteresSuccessMsg('Vos critères ont bien été sauvegardés.');
    } catch (e) {
      setCriteresSuccessMsg("Erreur lors de la sauvegarde des critères.");
    } finally {
      setCriteresSaving(false);
    }
  };

  // Fonction pour vérifier les champs et ouvrir le modal de confirmation
  const handleConfirmSubmit = () => {
    if (validateCriteresForm()) {
      setIsConfirmModalOpen(true);
    } else {
      const errorElement = document.querySelector('.error');
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  const handleSaveCandidature = async () => {
    try {
      const commandeId = window.location.pathname.split('/').pop() || '';
      const token = localStorage.getItem('token');
      const formattedArticles = (commande.articlesPrestataire || []).map((ap, index) => ({
        id: Number(commande.articles?.[index].id),
        prixHT: ap.prixHT
      }));

      const submissionData = {
        reponseTechnique: commande.reponseTechnique,
        criteres: commande.criteres,
        articles: formattedArticles,
        departementId: departementInfo?.id,
        regionId: departementInfo?.region?.id,
        id_signataire: commande.id_signataire,
        type_signataire: commande.type_signataire
      };

      console.log("[Candidature] Données de sauvegarde:", submissionData);
      console.log("[Candidature] Informations du signataire envoyées:", {
        id_signataire: submissionData.id_signataire,
        type_signataire: submissionData.type_signataire
      });
      
      const saveCandidature = await fetch(`${process.env.REACT_APP_API_URL}/subsequents/commande/${commandeId}/save`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submissionData)
      });
      if (!saveCandidature.ok) {
        alert('Une erreur est survenue lors de la sauvegarde de votre candidature');
        throw new Error('Erreur lors de la sauvegarde de la candidature');
      } else {
        alert('Candidature sauvegardée avec succès');
        navigate('/commandes');
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde de la candidature:', error);
      alert('Une erreur est survenue lors de la sauvegarde de votre candidature');
    }
  }

  // Fonction pour soumettre la candidature
  const handleSubmitCandidature = async () => {
    try {
      const commandeId = window.location.pathname.split('/').pop() || '';
      const token = localStorage.getItem('token');

      if (!token) throw new Error('Token non trouvé, veuillez vous reconnecter.');

      const submissionData = {
        reponseTechnique: commande.reponseTechnique,
        criteres: commande.criteres,
        articles: commande.articlesPrestataire,
        departementId: departementInfo?.id,
        regionId: departementInfo?.region?.id,
        id_signataire: commande.id_signataire,
        type_signataire: commande.type_signataire
      };

      console.log("[Candidature] Données soumises pour candidature:", submissionData);
      console.log("[Candidature] Informations du signataire envoyées:", {
        id_signataire: submissionData.id_signataire,
        type_signataire: submissionData.type_signataire
      });
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/subsequents/${commandeId}/candidater`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submissionData)
      });

      if (!response.ok) {
        let errorMessage = 'Erreur lors de la soumission de la candidature';
        try {
          const data = await response.json();
          if (data?.message) errorMessage = data.message;
        } catch {
          const text = await response.text();
          if (text) errorMessage = text;
        }
        throw new Error(`API Error (${response.status}): ${errorMessage}`);
      }

      alert('Candidature soumise avec succès');
      navigate('/commandes');
    } catch (error) {
      if (error instanceof TypeError) {
        alert(`Erreur réseau : ${error.message}`);
      } else {
        alert(`Erreur : ${error.message}`);
      }
    }
  };

  // États pour la gestion des signataires
  const [signataires, setSignataires] = useState<any[]>([]);
  const [signatairesLoading, setSignatairesLoading] = useState<boolean>(false);
  const [signatairesError, setSignatairesError] = useState<string | null>(null);

  // Ajouter cet état pour maintenir le signataire actuellement sélectionné
  const [selectedSignataireUser, setSelectedSignataireUser] = useState<number | null>(null);

  // Charger les signataires une seule fois quand l'entité est disponible
  useEffect(() => {
    if (!commande?.id_entite) return;
    
    const fetchSignataires = async () => {
      setSignatairesLoading(true);
      setSignatairesError(null);
      
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/entites/${commande.id_entite}/signataires`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`Erreur ${response.status} lors de la récupération des signataires`);
        }

        const data = await response.json();
        if (data.status === "success" && Array.isArray(data.data)) {
          console.log("[Candidature] Signataires reçus:", data.data);
          setSignataires(data.data);
          
          // Si un signataire est déjà sélectionné dans la commande, retrouver son id_user
          if (commande.id_signataire && commande.type_signataire) {
            const matchingUser = data.data.find(s => 
              s.id === commande.id_signataire && s.type_signataire === commande.type_signataire
            );
            
            if (matchingUser) {
              console.log("[Candidature] Signataire correspondant trouvé:", matchingUser);
              setSelectedSignataireUser(matchingUser.id_user);
            } else if (data.data.length > 0) {
              // Si pas de correspondance mais des signataires disponibles, prendre le premier
              console.log("[Candidature] Pas de signataire correspondant, sélection du premier");
              setSelectedSignataireUser(data.data[0].id_user);
              setCommande(prev => ({
                ...prev,
                id_signataire: data.data[0].id,
                type_signataire: data.data[0].type_signataire
              }));
            }
          } else if (data.data.length > 0) {
            // Aucun signataire sélectionné, prendre le premier
            console.log("[Candidature] Aucun signataire sélectionné, prendre le premier");
            setSelectedSignataireUser(data.data[0].id_user);
            setCommande(prev => ({
              ...prev,
              id_signataire: data.data[0].id,
              type_signataire: data.data[0].type_signataire
            }));
          }
        }
      } catch (error) {
        console.error("Erreur lors du chargement des signataires:", error);
        setSignatairesError("Impossible de charger la liste des signataires");
      } finally {
        setSignatairesLoading(false);
      }
    };

    fetchSignataires();
  }, [commande?.id_entite]);
  
  // Handler pour le changement de signataire
  const handleSignataireChange = (id: number, type_signataire: string) => {
    console.log("[Candidature] Nouveau signataire sélectionné:", { id, type_signataire });
    
    // Important: mettre à jour selectedSignataireUser en cherchant l'id_user correspondant
    const selectedSig = signataires.find(s => s.id === id && s.type_signataire === type_signataire);
    
    if (selectedSig) {
      console.log("[Candidature] Mise à jour du signataire avec:", selectedSig);
      setSelectedSignataireUser(selectedSig.id_user);
      
      setCommande(prev => {
        // Créer un nouvel objet pour forcer le re-rendu
        const updatedCommande = {
          ...prev,
          id_signataire: id,
          type_signataire: type_signataire
        };
        
        // Force log pour vérification
        console.log("[Candidature] Commande mise à jour:", updatedCommande);
        return updatedCommande;
      });
    }
  };
  
  console.log("commande.formulaire:", commande);

  return (
    <div className="tab-candidature">
      {/* Affichage des informations du département si disponibles */}
      {departementInfo && (
        <section>
          <h3 className='section-header'>Informations du département</h3>
          <div className="section-content">
            <div className="form-group">
              <label>Département</label>
              <div className="input-inactif">{departementInfo.nom || '-'}</div>
            </div>
            <div className="form-group">
              <label>Région</label>
              <div className="input-inactif">{departementInfo.region?.nom || '-'}</div>
            </div>
          </div>
        </section>
      )}

      {/* Première partie : Descriptif Administratif */}
      <section>
        <h3 className='section-header'>Descriptif Administratif</h3>
        <div className="section-content">
          <FormulaireAdministratif
            formData={commande.formulaire || {}}
            commande={commande}
            operation={commande.operation}
            viewMode={true}
            hideBesoin={true}
          />
        </div>
      </section>

      {/* Deuxième partie : Descriptif Technique */}
      <section>
        <h3 className='section-header'>Descriptif Technique</h3>
        <div className="section-content">
          <div className="form-group">
            <label>Définition du besoin</label>
            <div className="input-inactif definition-besoin">
              {commande.operation?.besoin || commande.formulaire?.besoin || "Pas précisé"}
            </div>
          </div>
          <div className="form-group">
            <label>Réponse Technique au Besoin <span style={{ color: 'red' }}>*</span></label>
            <textarea
              value={commande.reponseTechnique || ''}
              onChange={(e) => {
                setCommande(prev => ({
                  ...prev!,
                  reponseTechnique: e.target.value
                }));
              }}
              className="definition-besoin-textarea"
              placeholder="Décrivez votre réponse technique au besoin exprimé"
              rows={15}
            />
          </div>

          <div className="form-group critere-section">
            <label>
              Critère Organisation <span className="required">*</span>
            </label>
            <textarea
              value={commande.criteres?.organisation?.reponse || commande.criteres?.organisation?.reponse === '' ? commande.criteres?.organisation?.reponse : ''}
              onChange={e => handleCriteresChange('organisation', e.target.value)}
              placeholder="Décrivez votre organisation pour ce marché"
              className={criteresErrors.organisation ? 'error' : ''}
            />
            {criteresErrors.organisation && <span className="error-message">{criteresErrors.organisation}</span>}
          </div>
          <div className="form-group critere-section">
            <label>
              Critère Moyens mis en oeuvre<span className="required">*</span>
            </label>
            <textarea
              value={commande.criteres?.moyens?.reponse || ''}
              onChange={e => handleCriteresChange('moyens', e.target.value)}
              placeholder="Décrivez les moyens que vous mobilisez"
              className={criteresErrors.moyens ? 'error' : ''}
            />
            {criteresErrors.moyens && <span className="error-message">{criteresErrors.moyens}</span>}
          </div>
          <div className="form-group critere-section">
            <label>
              Critère Social & Environnemental <span className="required">*</span>
            </label>
            <textarea
              value={commande.criteres?.social?.reponse || ''}
              onChange={e => handleCriteresChange('social', e.target.value)}
              placeholder="Décrivez vos engagements sociaux et environnementaux"
              className={criteresErrors.social ? 'error' : ''}
            />
            {criteresErrors.social && <span className="error-message">{criteresErrors.social}</span>}
          </div>
        </div>
      </section>

      {/* Troisième partie : Descriptif Financier avec prix HT editable */}
      <section>
        <h3 className='section-header'>Offre Financière</h3>
        <div className="section-content">
          <table className="articles-table">
            <thead>
              <tr>
                <th className='financier-numero-article'>N°</th>
                <th>Description de la prestation</th>
                <th className="financier-prix">Prix forfaitaire HT</th>
              </tr>
            </thead>
            <tbody>
              {(commande.articles || []).map((article, index) => (
                <tr key={article.numero || index}>
                  <td className='financier-numero-article'>{article.code}</td>
                  <td>{article.nom}</td>
                  <td className="financier-prix">
                    <div className="prix-input-container">
                      <NumericFormat
                        value={commande.articlesPrestataire?.[index]?.prixHT ?? ''}
                        thousandSeparator=" "
                        decimalSeparator=","
                        decimalScale={2}
                        fixedDecimalScale
                        allowNegative={false}
                        allowLeadingZeros={false}
                        isNumericString
                        placeholder='0,00'
                        className="prix-input"
                        isAllowed={(values) => {
                          const { floatValue } = values;
                          return floatValue === undefined || floatValue <= 1000000000;
                        }}
                        onFocus={(e) => {
                          const valueStr = e.target.value;
                          const commaIndex = valueStr.indexOf(',');
                          if (commaIndex !== -1) {
                            e.target.setSelectionRange(commaIndex + 1, valueStr.length);
                          }
                        }}
                        onValueChange={(vals: NumberFormatValues) => {
                          const newArts = [
                            ...(commande.articlesPrestataire || []),
                          ];
                          if (!newArts[index]) {
                            newArts[index] = {
                              id: article.numero,
                              prixHT: 0,
                            };
                          }
                          newArts[index].prixHT = vals.floatValue ?? 0;
                          setCommande((prev) => ({
                            ...prev!,
                            articlesPrestataire: newArts,
                          }));
                        }}
                      />
                      <span className="prix-currency">€</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="total-section">
            <div className="total-row">
              <span>Total DPGF HT </span>
              <span>
                {totalHT.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Section pour les informations de signature */}
      <section>
        <h3 className='section-header'>Informations de signature</h3>
        <div className="section-content">
          <div className="form-group">
            <label>Signataire <span style={{ color: 'red' }}>*</span></label>
            <DropDownSignataires
              signataires={signataires}
              value={selectedSignataireUser} // Utiliser l'état local selectedSignataireUser
              onChange={handleSignataireChange}
              loading={false}
              error={null}
            />
            {(!commande.id_signataire && commande.status && commande.status.id < 4) && 
              <span className="error-message">Veuillez sélectionner un signataire</span>
            }
          </div>
        </div>
      </section>

      {commande.status && commande.status.id < 4 ? (
        <div className="action-buttons">
          <Button
            value="Enregistrer et quitter"
            variant="secondary"
            onClick={handleSaveCandidature}
          />
          <Button
            value="PDF"
            icon={<PdfIcon />}
            variant="secondary"
            onClick={handleExport}
          />
          <Button
            value="Soumettre la candidature"
            variant="primary"
            onClick={handleConfirmSubmit}
          />
        </div>
      ) : (
        <></>
      )}

      {/* Modal de confirmation pour soumettre la candidature */}
      <ValidationPopUp
        isOpen={isConfirmModalOpen}
        title={"Dépôt de la candidature"}
        text="Vous vous apprêtez à déposer votre candidature. Il n'est plus possible de modifier votre offre après cette étape. Voulez-vous continuer ?"
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={() => {
          setIsConfirmModalOpen(false);
          handleSubmitCandidature();
        }}
        cancelLabel="Non"
        confirmLabel="Oui"
      />
    </div>
  );
};

export default Candidature;